import { useState, useEffect, useRef, useMemo } from "react";
import { flushSync } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, OfficialStyle, CreateOfficialStyleInput, ImageAnalysisResult } from "@/lib/api";
import { cn } from "@/lib/cn";
import { useToast } from "@/components/ui/Toast";
import ConfirmModal from "@/components/ui/ConfirmModal";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import {
  ArrowLeft,
  Save,
  Loader2,
  Upload,
  X,
  Sparkles,
  Info,
  Wand2,
  RefreshCw,
  ImagePlus,
  ArrowUp,
} from "lucide-react";

const categories = [
  "簡報",
  "插畫",
  "海報",
  "社群媒體",
  "圖表",
  "概念圖",
  "UI 設計",
  "其他",
];

const statusOptions: { value: OfficialStyle["status"]; label: string }[] = [
  { value: "DRAFT", label: "草稿" },
  { value: "REVIEW", label: "審核中" },
  { value: "PUBLISHED", label: "已發布" },
  { value: "ARCHIVED", label: "已封存" },
];

const createInitialFormData = (): CreateOfficialStyleInput => ({
  name: "",
  description: "",
  triggerScene: "",
  prompt: "",
  referenceImages: [],
  category: "簡報",
  tags: [],
  status: "DRAFT",
  isEnabled: true,
  sortOrder: 0,
});

export default function StyleEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const isNew = !id;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<CreateOfficialStyleInput>(createInitialFormData);
  // Snapshot of the persisted/initial form state, used to detect unsaved edits.
  const [baseline, setBaseline] = useState<CreateOfficialStyleInput>(createInitialFormData);
  // Set to true once a save succeeds, so navigating away afterwards is allowed.
  const [justSaved, setJustSaved] = useState(false);
  // Field-level validation errors keyed by field name.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [tagInput, setTagInput] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingIndex, setGeneratingIndex] = useState<number | null>(null);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizationResult, setOptimizationResult] = useState<{
    optimizedPrompt: string;
    improvements: string[];
    suggestions: string[];
  } | null>(null);

  // Image analysis state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<ImageAnalysisResult | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const analyzeFileInputRef = useRef<HTMLInputElement>(null);

  // Check if AI generation is available
  const { data: generateStatus } = useQuery({
    queryKey: ["generate-status"],
    queryFn: api.getGenerateStatus,
  });

  const { data: existingStyle, isLoading: isLoadingStyle } = useQuery({
    queryKey: ["style", id],
    queryFn: () => api.getStyle(id!),
    enabled: !isNew,
  });

  useEffect(() => {
    if (existingStyle) {
      const next: CreateOfficialStyleInput = {
        name: existingStyle.name,
        description: existingStyle.description || "",
        triggerScene: existingStyle.triggerScene,
        prompt: existingStyle.prompt,
        referenceImages: existingStyle.referenceImages,
        category: existingStyle.category,
        tags: existingStyle.tags,
        status: existingStyle.status,
        isEnabled: existingStyle.isEnabled,
        sortOrder: existingStyle.sortOrder,
      };
      setFormData(next);
      setBaseline(next);
    }
  }, [existingStyle]);

  // Dirty when the form differs from its loaded/initial baseline (and not just saved).
  const isDirty = useMemo(
    () => !justSaved && JSON.stringify(formData) !== JSON.stringify(baseline),
    [formData, baseline, justSaved]
  );

  const blocker = useUnsavedChanges(isDirty);

  const createMutation = useMutation({
    mutationFn: api.createStyle,
    onSuccess: () => {
      // Flush synchronously so the unsaved-changes blocker sees the form as
      // clean before we navigate (otherwise the guard modal would fire).
      flushSync(() => setJustSaved(true));
      queryClient.invalidateQueries({ queryKey: ["styles"] });
      toast.success("已建立模板");
      navigate("/styles");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "建立失敗，請重試");
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: CreateOfficialStyleInput) => api.updateStyle(id!, data),
    onSuccess: () => {
      // Flush synchronously so the unsaved-changes blocker sees the form as
      // clean before we navigate (otherwise the guard modal would fire).
      flushSync(() => setJustSaved(true));
      queryClient.invalidateQueries({ queryKey: ["styles"] });
      queryClient.invalidateQueries({ queryKey: ["style", id] });
      toast.success("已儲存變更");
      navigate("/styles");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "儲存失敗，請重試");
    },
  });

  /**
   * Validate required fields before submitting. Returns the first missing
   * field's name so we can scroll to / focus it.
   */
  const validate = (): { ok: boolean; firstInvalid?: string } => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = "請輸入名稱";
    if (!formData.triggerScene.trim()) errors.triggerScene = "請輸入觸發情景";
    if (!formData.prompt.trim()) errors.prompt = "請輸入提示詞";
    setFieldErrors(errors);
    const firstInvalid = ["name", "triggerScene", "prompt"].find((f) => errors[f]);
    return { ok: Object.keys(errors).length === 0, firstInvalid };
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const { ok, firstInvalid } = validate();
    if (!ok) {
      toast.warning("請填寫所有必填欄位");
      if (firstInvalid) {
        const el = document.getElementById(`field-${firstInvalid}`);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
        (el as HTMLElement | null)?.focus();
      }
      return;
    }

    if (isNew) {
      createMutation.mutate(formData);
    } else {
      updateMutation.mutate(formData);
    }
  };

  /**
   * Back button simply navigates; the unsaved-changes blocker intercepts it and
   * shows the confirmation modal when there are pending edits.
   */
  const handleBack = () => {
    navigate("/styles");
  };

  const handleImageUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setIsUploading(true);
    try {
      const fileArray = Array.from(files);
      const { urls } = await api.uploadImages(fileArray);
      setFormData((prev) => ({
        ...prev,
        referenceImages: [...(prev.referenceImages || []), ...urls],
      }));
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error(error instanceof Error ? error.message : "圖片上傳失敗，請重試");
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = async (index: number) => {
    const imageUrl = formData.referenceImages?.[index];
    // Delete from R2 if it's a generated/uploaded image
    if (imageUrl) {
      try {
        await api.deleteGeneratedImage(imageUrl);
      } catch (error) {
        console.error("Failed to delete image from storage:", error);
        // Continue with removal even if delete fails
      }
    }
    setFormData((prev) => ({
      ...prev,
      referenceImages: prev.referenceImages?.filter((_, i) => i !== index),
    }));
  };

  /**
   * Generate a new showcase image using AI
   */
  const handleGenerateImage = async () => {
    if (!formData.prompt || !formData.name) {
      toast.warning("請先填寫名稱和提示詞");
      return;
    }

    setIsGenerating(true);
    setGeneratingIndex(null);

    try {
      const result = await api.generateShowcaseImage({
        stylePrompt: formData.prompt,
        styleName: formData.name,
      });

      if (result.success && result.data) {
        setFormData((prev) => ({
          ...prev,
          referenceImages: [...(prev.referenceImages || []), result.data!.url],
        }));
      } else {
        toast.error(result.error || "生成失敗，請重試");
      }
    } catch (error) {
      console.error("Generate failed:", error);
      toast.error(error instanceof Error ? error.message : "生成失敗，請重試");
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * Regenerate a specific image (delete old, generate new)
   */
  const handleRegenerateImage = async (index: number) => {
    if (!formData.prompt || !formData.name) {
      toast.warning("請先填寫名稱和提示詞");
      return;
    }

    const oldImageUrl = formData.referenceImages?.[index];
    if (!oldImageUrl) return;

    setIsGenerating(true);
    setGeneratingIndex(index);

    try {
      const result = await api.generateShowcaseImage({
        stylePrompt: formData.prompt,
        styleName: formData.name,
        oldImageUrl, // This will delete the old image
      });

      if (result.success && result.data) {
        setFormData((prev) => ({
          ...prev,
          referenceImages: prev.referenceImages?.map((url, i) =>
            i === index ? result.data!.url : url
          ),
        }));
      } else {
        toast.error(result.error || "重新生成失敗，請重試");
      }
    } catch (error) {
      console.error("Regenerate failed:", error);
      toast.error(error instanceof Error ? error.message : "重新生成失敗，請重試");
    } finally {
      setIsGenerating(false);
      setGeneratingIndex(null);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...(prev.tags || []), tagInput.trim()],
      }));
      setTagInput("");
    }
  };

  const handleRemoveTag = (tag: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags?.filter((t) => t !== tag),
    }));
  };

  /**
   * Optimize the prompt using AI
   */
  const handleOptimizePrompt = async () => {
    if (!formData.prompt) {
      toast.warning("請先填寫提示詞");
      return;
    }

    setIsOptimizing(true);
    setOptimizationResult(null);

    try {
      const result = await api.optimizePrompt({
        prompt: formData.prompt,
        styleName: formData.name,
        triggerScene: formData.triggerScene,
      });

      if (result.success && result.data) {
        setOptimizationResult(result.data);
      } else {
        toast.error(result.error || "優化失敗，請重試");
      }
    } catch (error) {
      console.error("Optimize failed:", error);
      toast.error(error instanceof Error ? error.message : "優化失敗，請重試");
    } finally {
      setIsOptimizing(false);
    }
  };

  /**
   * Apply the optimized prompt
   */
  const handleApplyOptimizedPrompt = () => {
    if (optimizationResult?.optimizedPrompt) {
      setFormData((prev) => ({
        ...prev,
        prompt: optimizationResult.optimizedPrompt,
      }));
      setOptimizationResult(null);
    }
  };

  /**
   * Convert file to base64
   */
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  /**
   * Analyze image and generate style information
   */
  const handleAnalyzeImage = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.warning("請選擇圖片檔案");
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.warning("圖片大小不能超過 10MB");
      return;
    }

    setIsAnalyzing(true);
    setAnalysisResult(null);

    try {
      const base64 = await fileToBase64(file);
      const result = await api.analyzeImage(base64);

      if (result.success && result.data) {
        setAnalysisResult(result.data);
      } else {
        toast.error(result.error || "分析失敗，請重試");
      }
    } catch (error) {
      console.error("Analyze failed:", error);
      toast.error(error instanceof Error ? error.message : "分析失敗，請重試");
    } finally {
      setIsAnalyzing(false);
    }
  };

  /**
   * Apply analysis result to form
   */
  const handleApplyAnalysisResult = (applyAll: boolean) => {
    if (!analysisResult) return;

    if (applyAll) {
      // Apply all fields
      setFormData((prev) => ({
        ...prev,
        name: prev.name || analysisResult.name,
        triggerScene: prev.triggerScene || analysisResult.triggerScene,
        prompt: analysisResult.prompt,
        category: analysisResult.category,
        tags: [...new Set([...(prev.tags || []), ...analysisResult.tags])],
      }));
    } else {
      // Only apply prompt
      setFormData((prev) => ({
        ...prev,
        prompt: analysisResult.prompt,
      }));
    }
    setAnalysisResult(null);
  };

  /**
   * Handle drag events for image analysis
   */
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleAnalyzeImage(file);
    }
  };

  const handleAnalyzeFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleAnalyzeImage(file);
    }
    // Reset input value to allow selecting the same file again
    e.target.value = "";
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (!isNew && isLoadingStyle) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-ceramic-400" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={handleBack}
          aria-label="返回列表"
          className="p-2 hover:bg-ceramic-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-ceramic-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-ceramic-900">
            {isNew ? "新增模板" : `編輯模板: ${existingStyle?.name}`}
          </h1>
        </div>
        <button
          type="submit"
          form="styleForm"
          disabled={isSaving}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors disabled:opacity-50"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {isNew ? "建立" : "儲存"}
        </button>
      </div>

      <form id="styleForm" onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Info */}
        <section className="bg-white border border-ceramic-200 rounded-xl p-6">
          <h2 className="text-lg font-medium text-ceramic-900 mb-4">基本資訊</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 sm:col-span-1">
              <label htmlFor="field-name" className="block text-sm font-medium text-ceramic-700 mb-2">
                名稱 *
              </label>
              <input
                id="field-name"
                type="text"
                value={formData.name}
                onChange={(e) => {
                  setFormData((prev) => ({ ...prev, name: e.target.value }));
                  if (fieldErrors.name) setFieldErrors((p) => ({ ...p, name: "" }));
                }}
                aria-invalid={!!fieldErrors.name}
                className={cn(
                  "w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent",
                  fieldErrors.name ? "border-red-400" : "border-ceramic-200"
                )}
                placeholder="例：Notion Clean"
                required
              />
              {fieldErrors.name && (
                <p className="mt-1 text-xs text-red-500">{fieldErrors.name}</p>
              )}
            </div>

            <div className="col-span-2 sm:col-span-1">
              <label className="block text-sm font-medium text-ceramic-700 mb-2">
                分類 *
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value }))}
                className="w-full px-4 py-3 border border-ceramic-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-ceramic-700 mb-2">
                描述
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                className="w-full px-4 py-3 border border-ceramic-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                placeholder="簡短描述此風格的特色"
              />
            </div>

            <div className="col-span-2 sm:col-span-1">
              <label className="block text-sm font-medium text-ceramic-700 mb-2">
                狀態
              </label>
              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    status: e.target.value as OfficialStyle["status"],
                  }))
                }
                className="w-full px-4 py-3 border border-ceramic-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
              >
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-span-2 sm:col-span-1">
              <label className="block text-sm font-medium text-ceramic-700 mb-2">
                排序權重
              </label>
              <input
                type="number"
                value={formData.sortOrder}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, sortOrder: parseInt(e.target.value) || 0 }))
                }
                className="w-full px-4 py-3 border border-ceramic-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-ceramic-700 mb-2">
                標籤
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.tags?.map((tag) => (
                  <span
                    key={tag}
                    className="flex items-center gap-1 px-3 py-1 bg-ceramic-100 text-ceramic-700 rounded-full text-sm"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                  className="flex-1 px-4 py-2 border border-ceramic-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                  placeholder="輸入標籤後按 Enter"
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="px-4 py-2 bg-ceramic-100 text-ceramic-700 rounded-lg hover:bg-ceramic-200 transition-colors"
                >
                  新增
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Trigger Scene */}
        <section className="bg-white border border-ceramic-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-lg font-medium text-ceramic-900">觸發情景</h2>
            <div className="group relative">
              <Info className="w-4 h-4 text-ceramic-400 cursor-help" />
              <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-ceramic-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                描述何時應該使用此風格，例如：「當用戶需要製作商務簡報或工作報告時」
              </div>
            </div>
          </div>
          <textarea
            id="field-triggerScene"
            value={formData.triggerScene}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, triggerScene: e.target.value }));
              if (fieldErrors.triggerScene) setFieldErrors((p) => ({ ...p, triggerScene: "" }));
            }}
            aria-invalid={!!fieldErrors.triggerScene}
            className={cn(
              "w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent min-h-[120px] resize-y",
              fieldErrors.triggerScene ? "border-red-400" : "border-ceramic-200"
            )}
            placeholder="描述何時應該使用此風格..."
            required
          />
          {fieldErrors.triggerScene && (
            <p className="mt-1 text-xs text-red-500">{fieldErrors.triggerScene}</p>
          )}
        </section>

        {/* Prompt */}
        <section className="bg-white border border-ceramic-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-medium text-ceramic-900">提示詞</h2>
              <div className="group relative">
                <Info className="w-4 h-4 text-ceramic-400 cursor-help" />
                <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-ceramic-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                  AI 生成圖片時使用的核心提示詞，描述視覺風格、色彩、排版等特徵
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={handleOptimizePrompt}
              disabled={isOptimizing || !formData.prompt}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-accent border border-accent rounded-lg hover:bg-accent/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isOptimizing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              AI 優化建議
            </button>
          </div>
          <textarea
            id="field-prompt"
            value={formData.prompt}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, prompt: e.target.value }));
              if (fieldErrors.prompt) setFieldErrors((p) => ({ ...p, prompt: "" }));
            }}
            aria-invalid={!!fieldErrors.prompt}
            className={cn(
              "w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent min-h-[200px] resize-y font-mono text-sm",
              fieldErrors.prompt ? "border-red-400" : "border-ceramic-200"
            )}
            placeholder="Clean minimal design with ample white space..."
            required
          />
          {fieldErrors.prompt && (
            <p className="mt-1 text-xs text-red-500">{fieldErrors.prompt}</p>
          )}

          {/* AI Optimization Result */}
          {optimizationResult && (
            <div className="mt-4 p-4 bg-accent/5 border border-accent/20 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-ceramic-900 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-accent" />
                  AI 優化建議
                </h3>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setOptimizationResult(null)}
                    className="px-3 py-1 text-xs text-ceramic-600 hover:text-ceramic-900 transition-colors"
                  >
                    忽略
                  </button>
                  <button
                    type="button"
                    onClick={handleApplyOptimizedPrompt}
                    className="px-3 py-1 text-xs bg-accent text-white rounded hover:bg-accent/90 transition-colors"
                  >
                    套用優化
                  </button>
                </div>
              </div>

              {/* Optimized Prompt Preview */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-ceramic-600 mb-1">優化後的提示詞</label>
                <div className="p-3 bg-white border border-ceramic-200 rounded text-sm font-mono text-ceramic-700 max-h-[150px] overflow-y-auto whitespace-pre-wrap">
                  {optimizationResult.optimizedPrompt}
                </div>
              </div>

              {/* Improvements */}
              {optimizationResult.improvements.length > 0 && (
                <div className="mb-3">
                  <label className="block text-xs font-medium text-ceramic-600 mb-1">改進項目</label>
                  <ul className="space-y-1">
                    {optimizationResult.improvements.map((improvement, index) => (
                      <li key={index} className="flex items-start gap-2 text-xs text-ceramic-600">
                        <span className="text-green-500 mt-0.5">✓</span>
                        {improvement}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Suggestions */}
              {optimizationResult.suggestions.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-ceramic-600 mb-1">額外建議</label>
                  <ul className="space-y-1">
                    {optimizationResult.suggestions.map((suggestion, index) => (
                      <li key={index} className="flex items-start gap-2 text-xs text-ceramic-500">
                        <span className="text-accent mt-0.5">→</span>
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Image Analysis Upload Area */}
          <div
            className={cn(
              "mt-4 relative rounded-xl p-5 transition-all",
              "bg-ceramic-50/80 border border-dashed",
              isDragging
                ? "border-accent bg-accent/5"
                : "border-ceramic-300 hover:border-ceramic-400",
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              ref={analyzeFileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAnalyzeFileSelect}
              className="hidden"
            />

            <div className="flex items-start gap-4">
              {/* Upload Icon */}
              <div
                className={cn(
                  "w-12 h-12 rounded-full flex-shrink-0",
                  "bg-white border border-ceramic-200",
                  "flex items-center justify-center",
                  "cursor-pointer hover:bg-ceramic-50 transition-colors"
                )}
                onClick={() => analyzeFileInputRef.current?.click()}
              >
                {isAnalyzing ? (
                  <Loader2 className="w-5 h-5 animate-spin text-accent" />
                ) : (
                  <ImagePlus className="w-5 h-5 text-ceramic-400" />
                )}
              </div>

              {/* Upload Info */}
              <div className="flex-grow">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-sm font-medium text-ceramic-700">
                    不知道怎麼描述？上傳參考圖片
                  </h3>
                  <span
                    className="text-xs text-ceramic-400 cursor-pointer hover:text-ceramic-600"
                    onClick={() => analyzeFileInputRef.current?.click()}
                  >
                    拖放或點擊上傳
                  </span>
                </div>
                <p className="text-xs text-ceramic-500">
                  AI 將分析圖片結構、材質和光線，自動生成風格提示詞
                </p>
              </div>
            </div>

            {/* Analysis Result */}
            {analysisResult && (
              <div className="mt-4 pt-4 border-t border-dashed border-ceramic-300">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-ceramic-600 uppercase tracking-wider">
                    AI 分析結果
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setAnalysisResult(null)}
                      className="px-3 py-1 text-xs text-ceramic-600 hover:text-ceramic-900 transition-colors"
                    >
                      忽略
                    </button>
                    <button
                      type="button"
                      onClick={() => handleApplyAnalysisResult(false)}
                      className="px-3 py-1 text-xs border border-accent text-accent rounded hover:bg-accent/5 transition-colors"
                    >
                      僅套用提示詞
                    </button>
                    <button
                      type="button"
                      onClick={() => handleApplyAnalysisResult(true)}
                      className="px-3 py-1 text-xs bg-accent text-white rounded hover:bg-accent/90 transition-colors flex items-center gap-1"
                    >
                      <ArrowUp className="w-3 h-3" />
                      套用全部
                    </button>
                  </div>
                </div>

                <div className="space-y-3 text-sm">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-ceramic-500 mb-1">建議名稱</label>
                      <div className="px-3 py-2 bg-white border border-ceramic-200 rounded text-ceramic-700">
                        {analysisResult.name}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-ceramic-500 mb-1">建議分類</label>
                      <div className="px-3 py-2 bg-white border border-ceramic-200 rounded text-ceramic-700">
                        {analysisResult.category}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-ceramic-500 mb-1">建議觸發情景</label>
                    <div className="px-3 py-2 bg-white border border-ceramic-200 rounded text-ceramic-700 text-xs">
                      {analysisResult.triggerScene}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-ceramic-500 mb-1">建議提示詞</label>
                    <div className="px-3 py-2 bg-white border border-ceramic-200 rounded text-ceramic-700 font-mono text-xs max-h-[100px] overflow-y-auto whitespace-pre-wrap">
                      {analysisResult.prompt}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-ceramic-500 mb-1">建議標籤</label>
                    <div className="flex flex-wrap gap-1">
                      {analysisResult.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-0.5 bg-white border border-ceramic-200 rounded text-xs text-ceramic-600"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Reference Images */}
        <section className="bg-white border border-ceramic-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-ceramic-900">參考圖片</h2>
            <div className="flex items-center gap-2">
              {/* AI Generate Button */}
              {generateStatus?.available && (
                <button
                  type="button"
                  onClick={handleGenerateImage}
                  disabled={isGenerating || !formData.prompt || !formData.name}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-accent border border-accent rounded-lg hover:bg-accent/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGenerating && generatingIndex === null ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Wand2 className="w-4 h-4" />
                  )}
                  AI 生成圖片
                </button>
              )}
              {/* Upload Button */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-ceramic-700 border border-ceramic-200 rounded-lg hover:bg-ceramic-50 transition-colors disabled:opacity-50"
              >
                {isUploading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                上傳圖片
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => handleImageUpload(e.target.files)}
              className="hidden"
            />
          </div>

          {/* AI Generation Status Hint */}
          {!generateStatus?.available && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
              AI 圖片生成功能未啟用。請設定 CF_AI_WORKER_TOKEN 環境變數。
            </div>
          )}

          <div className="grid grid-cols-4 gap-4">
            {formData.referenceImages?.map((url, index) => (
              <div key={index} className="relative group aspect-video">
                <img
                  src={url}
                  alt={`Reference ${index + 1}`}
                  className={cn(
                    "w-full h-full aspect-video object-cover rounded-lg border border-ceramic-200",
                    isGenerating && generatingIndex === index && "opacity-50"
                  )}
                />
                {/* Loading overlay for regenerating */}
                {isGenerating && generatingIndex === index && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg">
                    <Loader2 className="w-8 h-8 animate-spin text-white" />
                  </div>
                )}
                {/* Action buttons */}
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {/* Regenerate Button */}
                  {generateStatus?.available && (
                    <button
                      type="button"
                      onClick={() => handleRegenerateImage(index)}
                      disabled={isGenerating || !formData.prompt || !formData.name}
                      className="p-1.5 bg-accent text-white rounded-full hover:bg-accent/90 transition-colors disabled:opacity-50"
                      title="重新生成"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {/* Delete Button */}
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(index)}
                    disabled={isGenerating && generatingIndex === index}
                    className="p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors disabled:opacity-50"
                    title="刪除"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading || isGenerating}
              className={cn(
                "aspect-square border-2 border-dashed border-ceramic-200 rounded-lg flex flex-col items-center justify-center gap-2 text-ceramic-400 hover:border-ceramic-300 hover:text-ceramic-500 transition-colors",
                (isUploading || isGenerating) && "opacity-50 cursor-not-allowed"
              )}
            >
              {isUploading ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <>
                  <Upload className="w-6 h-6" />
                  <span className="text-sm">上傳</span>
                </>
              )}
            </button>
          </div>
        </section>
      </form>

      {/* Unsaved changes guard */}
      <ConfirmModal
        open={blocker.state === "blocked"}
        title="尚未儲存變更"
        description="您在此頁面有未儲存的變更，離開後將會遺失。確定要離開嗎？"
        confirmLabel="離開不儲存"
        cancelLabel="留在此頁"
        destructive
        onConfirm={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
      />
    </div>
  );
}
